import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  HStack,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react"
import { Link as RouterLink } from "@tanstack/react-router"
import { FiCalendar, FiMapPin, FiUsers } from "react-icons/fi"
import type { PublicClassInfo } from "../types"

interface ClassCardProps {
  classInfo: PublicClassInfo
}

/**
 * Format a time string to 12-hour format.
 * Handles both 24-hour (HH:MM or HH:MM:SS) and 12-hour ("4:30 PM") input.
 */
function formatTime(time: string | null): string {
  if (!time) return ""
  const trimmed = time.trim()
  // If already in 12-hour format with AM/PM, return as-is
  if (/[AaPp][Mm]\s*$/.test(trimmed)) return trimmed
  // Otherwise parse 24-hour format (HH:MM or HH:MM:SS)
  const [hours, minutes] = trimmed.split(":")
  const hour = Number.parseInt(hours, 10)
  const ampm = hour >= 12 ? "PM" : "AM"
  const hour12 = hour % 12 || 12
  return `${hour12}:${minutes} ${ampm}`
}

/**
 * Format price as currency
 */
function formatPrice(price: number | null): string {
  if (price === null) return "Contact for pricing"
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(price)
}

/**
 * Get the color palette for spots available badge
 */
function getSpotsColorPalette(
  spotsAvailable: number | null,
  capacity: number | null,
): "green" | "orange" | "red" {
  if (spotsAvailable === null || spotsAvailable <= 0) return "red"
  if (capacity && spotsAvailable <= Math.ceil(capacity * 0.25)) return "orange"
  return "green"
}

/**
 * Get display text for spots available
 */
function getSpotsText(spotsAvailable: number | null): string {
  if (spotsAvailable === null) return "Contact for availability"
  if (spotsAvailable <= 0) return "Waitlist"
  return `${spotsAvailable} spot${spotsAvailable === 1 ? "" : "s"} left`
}

export function ClassCard({ classInfo }: ClassCardProps) {
  const {
    id,
    name,
    level,
    day_of_week,
    start_time,
    end_time,
    location,
    age_min,
    age_max,
    price,
    spots_available,
    capacity,
  } = classInfo

  const scheduleText =
    day_of_week && start_time && end_time
      ? `${day_of_week} ${formatTime(start_time)} - ${formatTime(end_time)}`
      : day_of_week || "Schedule TBD"

  const ageRangeText =
    age_min !== null && age_max !== null
      ? `Ages ${age_min}-${age_max}`
      : age_min !== null
        ? `Ages ${age_min}+`
        : age_max !== null
          ? `Ages up to ${age_max}`
          : null

  const spotsColor = getSpotsColorPalette(spots_available, capacity)
  const spotsText = getSpotsText(spots_available)

  return (
    <Card.Root
      transition="all 0.2s"
      _hover={{
        shadow: "lg",
        transform: "translateY(-2px)",
      }}
    >
      <Card.Body>
        <VStack align="stretch" gap={3}>
          {/* Header: Name and Level Badge */}
          <Flex justify="space-between" align="flex-start" gap={2}>
            <Text fontWeight="semibold" fontSize="lg">
              {name}
            </Text>
            {level && (
              <Badge colorPalette="blue" flexShrink={0}>
                {level}
              </Badge>
            )}
          </Flex>

          {/* Schedule */}
          <HStack gap={2} color="gray.600">
            <Icon as={FiCalendar} />
            <Text fontSize="sm">{scheduleText}</Text>
          </HStack>

          {/* Location */}
          {location && (
            <HStack gap={2} color="gray.600">
              <Icon as={FiMapPin} />
              <Text fontSize="sm">{location}</Text>
            </HStack>
          )}

          {/* Age Range */}
          {ageRangeText && (
            <HStack gap={2} color="gray.600">
              <Icon as={FiUsers} />
              <Text fontSize="sm">{ageRangeText}</Text>
            </HStack>
          )}

          {/* Price and Availability */}
          <Flex justify="space-between" align="center" mt={2}>
            <Text fontWeight="bold" fontSize="lg">
              {formatPrice(price)}
            </Text>
            <Badge colorPalette={spotsColor}>{spotsText}</Badge>
          </Flex>

          {/* Enroll Button */}
          <Box mt={2}>
            <RouterLink to="/signup" search={{ class: id }}>
              <Button
                colorPalette={spots_available !== null && spots_available > 0 ? "blue" : "orange"}
                width="full"
              >
                {spots_available !== null && spots_available > 0 ? "Enroll Now" : "Join Waitlist"}
              </Button>
            </RouterLink>
          </Box>
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}
